import { isFlexJobMode } from 'src/utils/jobMode';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import map from 'lodash/map';

import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import DataTable from 'src/components/DataTable';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import {
  useGetJobApplications,
  useUpdateJobFavourite,
  useUpdateReapplyShift,
} from 'src/pages/ProjectDetailsPage/api';
import { hoursToString } from 'src/utils/datetime';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import StudentLink from 'src/components/StudentLink';

import messages from '../../messages';
import { useStyles } from '../../JobDetails.styled';
import FavoriteButton from '../FavoriteButton/FavoriteButton';
import { JobApplicationTabProps } from '../JobApplicationsAll/JobApplicationsAll';

export default function JobApplicationsDenied({
  job,
  jobUuid,
  filters,
  candidate,
  onCandidateSelect,
}: JobApplicationTabProps) {
  const isFlex = isFlexJobMode(job?.jobMode);
  const { formatMessage } = useIntl();
  const classes = useStyles();

  const [params, setParams] = useState<Record<string, string | number>>({
    page: 1,
  });

  const { data: jobApplications, isLoading } = useGetJobApplications({
    jobUuid,
    type: 'denied',
    filters,
    params,
    enabled: Boolean(jobUuid),
  });

  const { mutate: updateJobFavorite, isPending: isFavoritePending } =
    useUpdateJobFavourite();
  const { mutate: reApplyShift } = useUpdateReapplyShift();

  const handleFavorite = useCallback(
    (favorite: boolean, uuid: string) => {
      if (isFavoritePending) return;

      void updateJobFavorite({ jobUuid, applicationUuid: uuid, favorite });
    },
    [jobUuid, isFavoritePending],
  );

  const handleReapply = useCallback(
    (applicationUuid: string) => {
      reApplyShift({ jobUuid, applicationUuid });
    },
    [jobUuid],
  );

  const columns: Array<{
    id: keyof UCM.JobApplicationType | string;
    label: string | React.ReactNode;
  }> = useMemo(() => {
    const excludeColumns = isFlex ? ['actions'] : [];

    const allColumns = [
      {
        id: 'applicant',
        label: (
          <Typography variant="inherit">
            {formatMessage(messages.applicantLabel)}
          </Typography>
        ),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant) return '-';
          const { id, fullName, uuid } = item.applicant;
          return (
            <Stack flexDirection={'row'} alignItems={'center'} gap={1}>
              <FavoriteButton
                handleChange={(active) => handleFavorite(active, item.uuid)}
                active={item.favoriteManager}
              />
              <StudentLink label={`${id} - ${fullName}`} uuid={uuid} />
            </Stack>
          );
        },
      },
      {
        id: 'appliedCount',
        label: formatMessage(messages.appliedToLabel),
        render: (appliedCount: number) => (
          <Typography variant="inherit">{appliedCount ?? '-'}</Typography>
        ),
        align: 'center',
      },
      {
        id: 'yearlyWorkdaysCount',
        label: formatMessage(messages.workdayLabel),
        render: (yearlyWorkdaysCount: number) => (
          <Typography variant="inherit">{yearlyWorkdaysCount}</Typography>
        ),
        align: 'center',
      },
      {
        id: 'cancelRate',
        label: formatMessage(messages.cancelRateLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant.applicationsCancelledRatio) return '-';
          return (
            <Typography variant="inherit">
              {item.applicant.applicationsCancelledRatio}%
            </Typography>
          );
        },
        align: 'center',
      },
      {
        id: 'timeToConfirm',
        label: formatMessage(messages.timeToConfirmLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant.confirmationTime) return '-';
          return (
            <Typography variant="inherit">
              {hoursToString(item.applicant.confirmationTime)}
            </Typography>
          );
        },
        align: 'center',
      },
      {
        id: 'initialScore',
        label: formatMessage(messages.initialScoreColumnLabel),
        size: 'small',
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant.initialRatingScore) return '-';
          return (
            <Typography variant="inherit">
              {item.applicant.initialRatingScore}
            </Typography>
          );
        },
        align: 'center',
      },
      {
        id: 'ratingScore',
        label: formatMessage(messages.ratingLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant.ratingScore) return '-';
          return (
            <Typography variant="inherit">
              {item.applicant.ratingScore}
            </Typography>
          );
        },
        align: 'center',
      },
      {
        id: 'prescreeningRating',
        label: formatMessage(messages.preScreenLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          const { prescreeningRating } = item.applicant;

          return (
            <Tooltip
              arrow
              title={
                <Stack spacing={1}>
                  {Object.entries(prescreeningRating).map(([key, value]) => (
                    <Typography
                      key={key}
                      variant="inherit"
                      textTransform="capitalize"
                    >
                      {key}: {value}
                    </Typography>
                  ))}
                </Stack>
              }
            >
              <Typography variant="inherit">
                {prescreeningRating.average}
              </Typography>
            </Tooltip>
          );
        },
        align: 'center',
      },
      {
        id: 'profile',
        label: formatMessage(messages.profileLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant?.status) return '-';
          let label = formatMessage(messages.completeLabel);
          if (item.applicant?.status?.code === 'required')
            label = formatMessage(messages.incompleteLabel);
          return <Typography variant="inherit">{label}</Typography>;
        },
        align: 'center',
      },
      {
        id: 'statuses',
        label: formatMessage(messages.statusReasonLabel),
        render: (_: unknown, item: UCM.JobApplicationType) => {
          if ((isFlex && !filters.shift_date) || !item.statuses?.length)
            return '-';
          return map(item.statuses, (status, index) => (
            <Tooltip
              key={index}
              arrow
              describeChild
              title={status.reason.title || ''}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={0.5}
                tabIndex={status.reason.title ? 0 : undefined}
              >
                <Typography variant="inherit" textTransform="capitalize">
                  {status.status}
                </Typography>
                {status.reason.title && (
                  <HelpOutlineIcon
                    color="action"
                    sx={{ fontSize: '1rem', transform: 'translateY(-1px)' }}
                  />
                )}
              </Stack>
            </Tooltip>
          ));
        },
        align: 'center',
      },
      {
        id: 'actions',
        label: '',
        align: 'center',
        render: (_: Object, item: UCM.JobApplicationType) => (
          <ConfirmationDialog
            maxWidth="sm"
            onConfirm={() => handleReapply(item.uuid)}
            message={formatMessage(messages.reapplyApplicationConfirmMessage)}
          >
            {(handleOpen) => (
              <Tooltip title={formatMessage(messages.reapplyTooltip)}>
                <span>
                  <IconButton
                    disabled={item.statusCode === 'unbooked'}
                    onClick={handleOpen}
                  >
                    <SettingsBackupRestoreIcon color="primary" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </ConfirmationDialog>
        ),
      },
    ];

    return allColumns.filter((column) => !excludeColumns.includes(column.id));
  }, [isFlex, filters.shift_date, handleFavorite, formatMessage]);

  const handlePagination = useCallback((_: unknown, page: number) => {
    setParams({ page: page + 1 });
  }, []);

  return (
    <Grid size={{ xs: 12 }}>
      <DataTable
        rows={jobApplications?.data}
        columns={columns}
        className={classes.tableContainer}
        loading={isLoading}
        activeRow={candidate}
        pressRow={onCandidateSelect}
        checkActiveKey="applicant.id"
        onPaginate={handlePagination}
        pagination={jobApplications?.meta}
      />
    </Grid>
  );
}
